import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { groundHeight, LAKE } from './Landscape';
import { HALL_WINDOWS } from './HallWindows';
import { roofHeightAt } from './Weather';
import { PUDDLES } from './Puddles';
import { makeDeer, makeRabbit, makeFox, makeBird, makeButterfly, makeFlowerClump, makeOwl } from './animalModels';
import { eggState, announceEgg } from '../../lib/eggs';

// Wildlife outside the glass hall, placed where the windows look out: deer, rabbits, foxes,
// songbirds, ravens, butterflies over wildflowers, and fireflies on calm nights.
//
// Who is out depends on the weather, the way real animals behave:
//   clear day  – deer graze, rabbits hop about, songbirds wheel overhead, butterflies visit flowers
//   cloudy     – no butterflies (they need sunshine to warm up for flight); a fox may be about
//   windy      – butterflies shelter in the grass; birds ride the gusts
//   fog        – deer, rabbits and a fox; no birds or butterflies
//   rain/storm – everyone runs: deer and foxes bolt for the forest, rabbits dash into their
//                burrows, birds fly off, butterflies drop into the grass
//   snow       – only winter animals: deer, white snowshoe hares, foxes and ravens; flowers go
//   night      – deer, rabbits and foxes (they are crepuscular/nocturnal); fireflies on calm nights
//
// Performance: one draw call per species (InstancedMesh). Legs, heads, tails and wings are moved
// in the vertex shader from per-instance values (aAnim), and one material is shared by all
// animals, so this adds just a single shader program. Absent animals are scaled to zero; a species
// with nobody out is skipped entirely. The wildflowers (same material) always stay in the scene,
// so the shared program is always compiled up front — no shader ever compiles mid-visit.

const IS_MOBILE = typeof window !== 'undefined' && (window.innerWidth < 768 || /iPhone|iPad|Android/i.test(navigator.userAgent));
const TAU = Math.PI * 2;
const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const wrapAngle = (a) => Math.atan2(Math.sin(a), Math.cos(a));
const ease = (v, target, rate, dt) => v + (target - v) * Math.min(1, rate * dt);

// A point just outside every window, with the outward direction (n) and the wall direction (t).
// Animals live in a "view corridor" in front of one window each, so every window has its own.
const WINDOW_SPOTS = [
  ...HALL_WINDOWS.north.map((x) => ({ x, z: -12, nx: 0, nz: -1 })),
  ...HALL_WINDOWS.east.map((x) => ({ x: 12, z: x, nx: 1, nz: 0 })),
  ...HALL_WINDOWS.west.map((x) => ({ x: -12, z: -x, nx: -1, nz: 0 })),
].map((s) => ({ ...s, tx: -s.nz, tz: s.nx }));
const fromSpot = (sp, out, lat) => [sp.x + sp.nx * out + sp.tx * lat, sp.z + sp.nz * out + sp.tz * lat];
const corridorHalfWidth = (out) => 1.5 + out * 0.3; // what a visitor a few metres from the window can see

const inLake = (x, z) => Math.hypot(x - LAKE.x, z - LAKE.z) < LAKE.r * 1.3;
const nearHall = (x, z) => Math.abs(x) < 14 && Math.abs(z) < 14;

// What the weather allows right now (thresholds on the smoothly blended weather).
const conditions = (w, n) => {
  const rainy = w.rain > 0.3 || w.storm > 0.3;
  const snowy = w.snow > 0.4;
  const night = n > 0.5;
  const foggy = w.fog > 0.6;
  const windy = w.wind > 0.55;
  return {
    deer: !rainy,
    rabbit: !rainy && !snowy,
    hare: !rainy && snowy,
    fox: !rainy && (snowy || night || foggy || w.cloud > 0.5),
    songbird: !rainy && !snowy && !foggy && !night,
    raven: !rainy && snowy && !night,
    butterfly: !rainy && !snowy && !night && !windy && w.cloud < 0.5 && w.fog < 0.5,
    firefly: night && !rainy && !snowy && !windy && w.fog < 0.6,
  };
};

// ------------------------------------------------------------------ shared animated material
const makeMaterial = () => {
  const m = new THREE.MeshStandardMaterial({ color: new THREE.Color(0.58, 0.58, 0.58), vertexColors: true, flatShading: true, roughness: 0.9, metalness: 0, envMapIntensity: 0.3, side: THREE.DoubleSide });
  // (the base colour darkens everything a little: the outdoor sun is strong, as for the terrain)
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
        attribute vec4 aAnim;   // x: gait/flap phase  y: stride or flap amplitude  z: head down or wing lift  w: tail phase or wing fold
        attribute float aPart;
        attribute vec3 aPivot;
        vec3 wlRotX(vec3 p, float a) { float c = cos(a), s = sin(a); return vec3(p.x, p.y * c - p.z * s, p.y * s + p.z * c); }
        vec3 wlRotY(vec3 p, float a) { float c = cos(a), s = sin(a); return vec3(p.x * c + p.z * s, p.y, -p.x * s + p.z * c); }
        vec3 wlRotZ(vec3 p, float a) { float c = cos(a), s = sin(a); return vec3(p.x * c - p.y * s, p.x * s + p.y * c, p.z); }`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        {
          vec3 q = transformed - aPivot;
          if (aPart > 0.5 && aPart < 1.5) q = wlRotX(q, aAnim.z * 1.5);                       // head down to graze
          else if (aPart > 1.5 && aPart < 5.5) {                                                // walking/running legs
            float off = (aPart < 2.5 || aPart > 4.5) ? 0.0 : 3.14159;
            q = wlRotX(q, sin(aAnim.x + off) * aAnim.y * 0.62);
          }
          else if (aPart > 5.5 && aPart < 6.5) q = wlRotY(wlRotX(q, 0.25 * sin(aAnim.w * 1.3)), 0.35 * sin(aAnim.w));  // tail
          else if (aPart > 6.5 && aPart < 8.5) {                                                // wings
            float side = aPart < 7.5 ? 1.0 : -1.0;
            q = wlRotZ(q, side * (sin(aAnim.x) * aAnim.y + aAnim.z) * (1.0 - aAnim.w));
            q = wlRotY(q, side * aAnim.w * 1.3);                                                // w: folded (perched bird)
          }
          else if (aPart > 8.5) q = wlRotX(q, sin(aAnim.x + (aPart < 9.5 ? 0.0 : 2.4)) * aAnim.y * 0.95); // hopping legs
          transformed = q + aPivot;
        }`);
  };
  m.customProgramCacheKey = () => 'wildlife';
  return m;
};

const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);
const makeMesh = (geometry, count, material, tint) => {
  const mesh = new THREE.InstancedMesh(geometry, material, Math.max(1, count));
  mesh.frustumCulled = false;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  const anim = new THREE.InstancedBufferAttribute(new Float32Array(Math.max(1, count) * 4), 4);
  anim.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('aAnim', anim);
  const c = new THREE.Color();
  for (let i = 0; i < mesh.count; i++) {
    mesh.setMatrixAt(i, HIDDEN);
    mesh.setColorAt(i, tint ? tint(c, i) : c.setRGB(1, 1, 1)); // every mesh has instance colours → one shared program
  }
  return mesh;
};

// ------------------------------------------------------------------ ground animals
// Distances are metres beyond the window wall: out = range they roam in, exit = forest edge.
const SPECIES = {
  deer:   { walk: 1.1, run: 8.5, stride: [1.5, 4.2], outMin: 6, outMax: 30, exit: 80, turn: 2.2, graze: 1.4, bound: 0.12 },
  rabbit: { walk: 1.4, run: 6.5, stride: [0.5, 1.5], outMin: 2.5, outMax: 15, burrow: true, turn: 4.5, graze: 0.55, hop: true },
  fox:    { walk: 1.5, run: 9, stride: [0.8, 2.4], outMin: 5, outMax: 26, exit: 76, turn: 3, graze: 0.45, bound: 0.08 },
};

const clampToZone = (x, z, spot, sp) => {
  const dx = x - spot.x, dz = z - spot.z;
  const out = clamp(dx * spot.nx + dz * spot.nz, sp.outMin, sp.outMax);
  const w = corridorHalfWidth(out);
  return fromSpot(spot, out, clamp(dx * spot.tx + dz * spot.tz, -w, w));
};
const zonePoint = (spot, sp) => {
  for (let i = 0; i < 12; i++) {
    const out = rand(sp.outMin, sp.outMax);
    const [x, z] = fromSpot(spot, out, rand(-1, 1) * corridorHalfWidth(out));
    if (!inLake(x, z)) return [x, z];
  }
  return fromSpot(spot, sp.outMin, 0);
};
const edgePoint = (spot, sp, extra = 0) => {
  for (let i = 0; i < 12; i++) {
    const out = sp.exit + extra;
    const [x, z] = fromSpot(spot, out, rand(-0.3, 0.3) * out);
    if (!inLake(x, z)) return [x, z];
  }
  return fromSpot(spot, sp.exit + extra, 0);
};
const outOf = (a) => (a.x - a.spot.x) * a.spot.nx + (a.z - a.spot.z) * a.spot.nz;

const newGround = (kind, group, i, size) => ({
  kind, group, spot: WINDOW_SPOTS[i % WINDOW_SPOTS.length], size,
  x: 0, z: 0, h: 0, speed: 0, target: 0, tx: 0, tz: 0,
  state: 'away', timer: 0, phase: Math.random() * TAU, tail: Math.random() * TAU, graze: 0, scale: 0, grow: 0,
});

function placeInZone(a, sp) {
  [a.x, a.z] = zonePoint(a.spot, sp);
  a.h = Math.random() * TAU;
}

function arrive(a, sp, instantly) {
  if (instantly) { // already out when the visitor arrives
    placeInZone(a, sp);
    a.state = 'graze'; a.timer = rand(1, 10); a.scale = 1; a.grow = 0;
  } else if (sp.burrow) { // pops out of its burrow
    placeInZone(a, sp);
    a.state = 'emerge'; a.timer = rand(0.8, 2); a.scale = 0; a.grow = 1.6;
  } else { // walks in from the forest edge
    [a.x, a.z] = edgePoint(a.spot, sp);
    [a.tx, a.tz] = zonePoint(a.spot, sp);
    a.h = Math.atan2(a.tx - a.x, a.tz - a.z);
    a.state = 'walk'; a.scale = 0; a.grow = 1;
  }
}

function flee(a, sp) {
  a.state = 'startle'; a.timer = rand(0.15, 1.1); // heads up, a moment's pause, then run
  if (sp.burrow) {
    // the burrow is a few metres away, in the open meadow
    const ang = Math.random() * TAU, d = rand(3, 7);
    [a.tx, a.tz] = clampToZone(a.x + Math.cos(ang) * d, a.z + Math.sin(ang) * d, a.spot, sp);
  } else {
    [a.tx, a.tz] = edgePoint(a.spot, sp, 12); // away from the hall, into the forest
  }
}

// Walk to the edge of a puddle in front of this animal's window (deer and foxes drink after rain).
function goToPuddle(a) {
  const near = PUDDLES.filter((pd) => WINDOW_SPOTS[pd.window] === a.spot);
  if (!near.length) return false;
  const pd = near[(Math.random() * near.length) | 0];
  const ang = Math.atan2(a.z - pd.z, a.x - pd.x); // approach from the animal's side
  const edge = pd.r * 0.75 + 0.6;
  a.px = pd.x; a.pz = pd.z;
  a.tx = pd.x + Math.cos(ang) * edge; a.tz = pd.z + Math.sin(ang) * edge;
  a.state = 'toPuddle';
  return true;
}

function updateGround(a, sp, want, dt, wind, puddles = 0) {
  if (a.state === 'away') {
    if (want) { a.timer -= dt; if (a.timer <= 0) arrive(a, sp, false); }
    return;
  }
  if (!want && !['startle', 'flee', 'hide'].includes(a.state)) flee(a, sp);
  if (want && (a.state === 'startle' || a.state === 'flee' || a.state === 'hide') && a.scale > 0.98 && a.grow === 0) {
    // weather turned nice again before it got away: settle down
    a.state = 'look'; a.timer = rand(1, 3);
  }

  let targetSpeed = 0, grazeTarget = 0;
  a.timer -= dt;
  switch (a.state) {
    case 'graze':
      grazeTarget = sp.graze;
      if (a.timer <= 0) { a.state = 'look'; a.timer = rand(1.5, 4); }
      break;
    case 'look':
      if (a.timer <= 0 && !sp.burrow && puddles > 0.3 && Math.random() < 0.4 && goToPuddle(a)) break; // thirsty after the rain
      if (a.timer <= 0) {
        const ang = Math.random() * TAU, d = sp.hop ? rand(1, 4) : rand(3, 11);
        [a.tx, a.tz] = clampToZone(a.x + Math.cos(ang) * d, a.z + Math.sin(ang) * d, a.spot, sp);
        if (inLake(a.tx, a.tz)) [a.tx, a.tz] = zonePoint(a.spot, sp);
        a.state = 'walk';
      }
      break;
    case 'walk':
      targetSpeed = sp.walk;
      if (Math.hypot(a.tx - a.x, a.tz - a.z) < 0.6) { a.state = 'graze'; a.timer = rand(4, 13) * (1 - 0.5 * wind); }
      break;
    case 'curious': // walking up to the window where a visitor stands still (secret)
      targetSpeed = sp.walk * 1.3;
      if (Math.hypot(a.tx - a.x, a.tz - a.z) < 0.4) {
        a.state = 'gaze'; a.timer = rand(12, 16);
        announceEgg('deer', 'A curious deer came up to the window to see you.', '🦌');
      }
      break;
    case 'gaze': // looking in at the visitor, head up, ears forward
      a.h += clamp(wrapAngle(Math.atan2(-a.spot.nx, -a.spot.nz) - a.h), -1.5 * dt, 1.5 * dt);
      if (a.timer <= 0) { a.state = 'look'; a.timer = rand(1, 2); }
      break;
    case 'toPuddle':
      targetSpeed = sp.walk;
      if (puddles < 0.08) { a.state = 'look'; a.timer = rand(1, 3); break; } // it dried up
      if (Math.hypot(a.tx - a.x, a.tz - a.z) < 0.4) { a.state = 'drink'; a.timer = rand(6, 12); }
      break;
    case 'drink':
      grazeTarget = sp.graze; // head down to the water
      a.h += clamp(wrapAngle(Math.atan2(a.px - a.x, a.pz - a.z) - a.h), -1.5 * dt, 1.5 * dt); // face the puddle
      if (a.timer <= 0 || puddles < 0.05) { a.state = 'look'; a.timer = rand(1.5, 4); }
      break;
    case 'emerge':
      if (a.timer <= 0) { a.state = 'look'; a.timer = rand(1, 3); }
      break;
    case 'startle':
      if (a.timer <= 0) a.state = 'flee';
      break;
    case 'flee': {
      targetSpeed = sp.run;
      const d = Math.hypot(a.tx - a.x, a.tz - a.z);
      if (sp.burrow ? d < 0.5 : outOf(a) > sp.exit) { a.state = 'hide'; a.grow = sp.burrow ? -2.5 : -1.5; }
      break;
    }
    case 'hide':
      targetSpeed = sp.burrow ? 0 : sp.run;
      if (a.scale <= 0) { a.state = 'away'; a.timer = rand(2, 14); a.grow = 0; }
      break;
    default:
  }

  // scale in/out (emerging from a burrow, vanishing into the forest)
  if (a.grow) {
    a.scale = clamp(a.scale + a.grow * dt, 0, 1);
    if (a.grow > 0 && a.scale >= 1) a.grow = 0;
  }

  a.speed = ease(a.speed, targetSpeed, a.state === 'flee' ? 3 : 1.6, dt);
  if (a.speed > 0.02) {
    const want = Math.atan2(a.tx - a.x, a.tz - a.z);
    const turn = sp.turn * (a.state === 'flee' ? 2 : 1) * dt;
    a.h += clamp(wrapAngle(want - a.h), -turn, turn);
    a.x += Math.sin(a.h) * a.speed * dt;
    a.z += Math.cos(a.h) * a.speed * dt;
    if (nearHall(a.x, a.z)) { const r = Math.hypot(a.x, a.z) || 1; a.x *= 14.5 / r; a.z *= 14.5 / r; }
  } else if (a.state === 'startle' || a.state === 'look') {
    // look around towards the hall now and then
    a.h += clamp(wrapAngle(Math.atan2(-a.x, -a.z) - a.h), -0.6 * dt, 0.6 * dt) * (a.state === 'look' ? 0.5 : 1.5);
  }
  const runness = clamp((a.speed - sp.walk) / (sp.run - sp.walk), 0, 1);
  const strideLen = sp.stride[0] + (sp.stride[1] - sp.stride[0]) * runness;
  a.phase += (a.speed / strideLen) * TAU * dt;
  a.graze = ease(a.graze, grazeTarget, 1.8, dt);
  a.tail += dt * (2 + a.speed * 2);
}

function writeGround(mesh, i, a, sp, m, q, p, s, up) {
  if (a.scale <= 0.001) { mesh.setMatrixAt(i, HIDDEN); return; }
  const moving = clamp(a.speed / sp.walk, 0, 1);
  const runness = clamp((a.speed - sp.walk) / (sp.run - sp.walk), 0, 1);
  let bob = 0;
  if (sp.hop) bob = Math.max(0, Math.sin(a.phase)) * (0.05 + 0.18 * runness) * moving;
  else bob = Math.abs(Math.sin(a.phase)) * (sp.bound || 0) * runness;
  p.set(a.x, groundHeight(a.x, a.z) + bob, a.z);
  q.setFromAxisAngle(up, a.h);
  s.setScalar(a.size * a.scale);
  m.compose(p, q, s);
  mesh.setMatrixAt(i, m);
  const anim = mesh.geometry.attributes.aAnim.array;
  anim[i * 4] = a.phase;
  anim[i * 4 + 1] = moving * (0.55 + 0.45 * runness);
  anim[i * 4 + 2] = a.graze;
  anim[i * 4 + 3] = a.tail;
}

// ------------------------------------------------------------------ component
export const Wildlife = ({ weatherRef, nightRef }) => {
  const counts = IS_MOBILE
    ? { does: 5, stags: 1, rabbits: 6, hares: 5, foxes: 2, songFlocks: 3, songPer: 5, ravenFlocks: 1, ravenPer: 5, butterflies: 10, fireflies: 50, flowers: 32 }
    : { does: 6, stags: 2, rabbits: 9, hares: 7, foxes: 2, songFlocks: 4, songPer: 6, ravenFlocks: 2, ravenPer: 4, butterflies: 16, fireflies: 80, flowers: 50 };

  const world = useMemo(() => {
    const material = makeMaterial();
    const songTints = ['#a07850', '#8a6a48', '#5a6a8a', '#b08a50', '#6f5a45'].map((c) => new THREE.Color(c));
    const flyTints = ['#ff7a1a', '#ffffff', '#5b8cff', '#ffe04a', '#ff9a3a'].map((c) => new THREE.Color(c));

    const does = Array.from({ length: counts.does }, (_, i) => newGround('deer', 'deer', i, rand(0.85, 1)));
    const stags = Array.from({ length: counts.stags }, (_, i) => newGround('deer', 'deer', i * 3 + 6, rand(1.0, 1.12)));
    const rabbits = [
      ...Array.from({ length: counts.rabbits }, (_, i) => newGround('rabbit', 'rabbit', i, rand(0.9, 1.1))),
      ...Array.from({ length: counts.hares }, (_, i) => newGround('rabbit', 'hare', i + 3, rand(1.1, 1.3))),
    ];
    const foxes = Array.from({ length: counts.foxes }, (_, i) => newGround('fox', 'fox', i * 3 + 1, rand(0.95, 1.05)));

    const birds = [];
    const flocks = [];
    const addFlocks = (n, per, raven) => {
      for (let f = 0; f < n; f++) {
        const flock = { raven, start: birds.length, count: per, R: rand(raven ? 26 : 22, raven ? 55 : 46), H: rand(raven ? 8 : 5, raven ? 20 : 14), dir: Math.random() < 0.5 ? -1 : 1, theta: Math.random() * TAU, omega: raven ? rand(0.05, 0.08) : rand(0.08, 0.14), out: 1, seed: Math.random() * 10, mode: 'fly', timer: rand(6, 25) };
        flocks.push(flock);
        for (let k = 0; k < per; k++) {
          birds.push({ flock, ox: rand(-4, 4), oy: rand(-1.5, 1.5), oz: rand(-4, 4), ph: Math.random() * TAU, phase: Math.random() * TAU, rate: raven ? rand(6, 8) : rand(17, 23), size: raven ? rand(2.4, 2.8) : rand(1.3, 1.6), px: 0, pz: 0, h: 0, land: 0, landRate: rand(0.18, 0.3), sx: 0, sz: 0, roof: false, act: 0, peckT: 0, hopT: 0, tint: raven ? new THREE.Color('#151518') : songTints[k % songTints.length] });
        }
      }
    };
    addFlocks(counts.songFlocks, counts.songPer, false);
    addFlocks(counts.ravenFlocks, counts.ravenPer, true);

    const flies = Array.from({ length: counts.butterflies }, (_, i) => ({
      spot: WINDOW_SPOTS[i % WINDOW_SPOTS.length], x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, tx: 0, ty: 0, tz: 0,
      state: 'away', timer: 0, phase: Math.random() * TAU, scale: 0, h: 0, tint: flyTints[i % flyTints.length], size: rand(2, 2.6),
    }));

    // Wildflower clumps in front of the windows (butterflies visit them).
    const flowerSpots = [];
    for (let i = 0; i < counts.flowers; i++) {
      const sp = WINDOW_SPOTS[i % WINDOW_SPOTS.length];
      const out = 2.5 + Math.pow(Math.random(), 1.4) * 22, along = rand(-7, 7) * (0.4 + out / 20);
      const x = sp.x + sp.nx * out + sp.tx * along, z = sp.z + sp.nz * out + sp.tz * along;
      if (nearHall(x, z) || inLake(x, z)) continue;
      flowerSpots.push({ x, z, s: rand(0.8, 1.4), rot: Math.random() * TAU });
    }

    const meshes = {
      does: makeMesh(makeDeer(), does.length, material, (c) => c.setScalar(rand(0.88, 1.08))),
      stags: makeMesh(makeDeer({ antlers: true }), stags.length, material, (c) => c.setScalar(rand(0.85, 1))),
      rabbits: makeMesh(makeRabbit(), rabbits.length, material, (c, i) => (rabbits[i].group === 'hare' ? c.setRGB(1, 1, 1.03) : c.setRGB(0.6, 0.43, 0.28).multiplyScalar(rand(0.85, 1.1)))),
      foxes: makeMesh(makeFox(), foxes.length, material),
      birds: makeMesh(makeBird(), birds.length, material, (c, i) => c.copy(birds[i].tint)),
      flies: makeMesh(makeButterfly(), flies.length, material, (c, i) => c.copy(flies[i].tint)),
      flowers: makeMesh(makeFlowerClump(0), flowerSpots.length, material),
      owl: makeMesh(makeOwl(), 1, material), // secret: visits between 2 and 4 am (see lib/eggs.js)
    };

    // Fireflies: glowing points, additive and opaque-pass so they show through the glass roof too.
    const ffPos = new Float32Array(counts.fireflies * 3), ffCol = new Float32Array(counts.fireflies * 3);
    const ffGeo = new THREE.BufferGeometry();
    ffGeo.setAttribute('position', new THREE.BufferAttribute(ffPos, 3));
    ffGeo.setAttribute('color', new THREE.BufferAttribute(ffCol, 3));
    ffGeo.setDrawRange(0, 0);
    const fireflies = Array.from({ length: counts.fireflies }, (_, i) => {
      const sp = WINDOW_SPOTS[i % WINDOW_SPOTS.length];
      const out = rand(2.5, 18), along = rand(-7, 7);
      return { bx: sp.x + sp.nx * out + sp.tx * along, bz: sp.z + sp.nz * out + sp.tz * along, by: rand(0.3, 2.6), p1: rand(0, TAU), p2: rand(0, TAU), p3: rand(0, TAU), rate: rand(0.7, 1.5), ph: rand(0, TAU) };
    });
    const glow = (() => {
      const c = document.createElement('canvas'); c.width = c.height = 32;
      const ctx = c.getContext('2d'); const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
      g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.3, 'rgba(255,255,255,0.6)'); g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, 32, 32);
      return new THREE.CanvasTexture(c);
    })();
    const ffMat = new THREE.PointsMaterial({ size: 0.5, map: glow, vertexColors: true, transparent: false, depthWrite: false, blending: THREE.AdditiveBlending });
    const ffPoints = new THREE.Points(ffGeo, ffMat);
    ffPoints.frustumCulled = false;
    // the secret owl's glowing eyes (same kind of material as the fireflies: no extra shader)
    const eyeGeo = new THREE.BufferGeometry();
    eyeGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
    eyeGeo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(6), 3));
    eyeGeo.setDrawRange(0, 0);
    const eyeMat = new THREE.PointsMaterial({ size: 0.3, map: glow, vertexColors: true, transparent: false, depthWrite: false, blending: THREE.AdditiveBlending });
    const eyePoints = new THREE.Points(eyeGeo, eyeMat);
    eyePoints.frustumCulled = false;

    return { material, meshes, does, stags, rabbits, foxes, birds, flocks, flies, flowerSpots, fireflies, ffPoints, ffGeo, ffMat, glow, eyePoints, eyeGeo, eyeMat, ffPresence: 0, snowCover: 0, flowerShown: -1, started: false };
  }, []); // eslint-disable-line

  useEffect(() => () => {
    Object.values(world.meshes).forEach((m) => { m.geometry.dispose(); m.dispose(); });
    world.material.dispose(); world.ffGeo.dispose(); world.ffMat.dispose(); world.glow.dispose(); world.eyeGeo.dispose(); world.eyeMat.dispose();
  }, [world]);

  const { camera } = useThree();
  const watcher = useRef({ x: 0, z: 0, still: 0, lastSpot: null, lastAt: -1e9 });
  const tmp = useRef({ m: new THREE.Matrix4(), q: new THREE.Quaternion(), p: new THREE.Vector3(), s: new THREE.Vector3(), up: new THREE.Vector3(0, 1, 0), e: new THREE.Euler() });

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.1);
    const t = state.clock.elapsedTime;
    const w = weatherRef.current;
    const n = nightRef ? nightRef.current : 0;
    const ok = conditions(w, n);
    const W = world;
    const { m, q, p, s, up, e } = tmp.current;

    // First frame: animals that belong in this weather are already out.
    if (!W.started) {
      W.started = true;
      [...W.does, ...W.stags, ...W.rabbits, ...W.foxes].forEach((a) => { if (ok[a.group]) arrive(a, SPECIES[a.kind], true); else a.timer = rand(1, 12); });
      W.flocks.forEach((f) => { f.out = ok[f.raven ? 'raven' : 'songbird'] ? 0 : 1; });
      W.flies.forEach((b) => { if (ok.butterfly) { b.state = 'fly'; b.scale = 1; pickFlyTarget(b, false); b.x = b.tx; b.y = b.ty; b.z = b.tz; } else b.timer = rand(0, 8); });
    }

    // Secret: a visitor standing still at a window for a few seconds gets a curious deer at the glass.
    const wt = watcher.current;
    const moved = Math.hypot(camera.position.x - wt.x, camera.position.z - wt.z);
    wt.x = camera.position.x; wt.z = camera.position.z;
    wt.still = moved < 0.02 ? wt.still + dt : 0;
    if (wt.still > 6 && ok.deer) {
      const spot = WINDOW_SPOTS.find((sp) => Math.hypot(camera.position.x - (sp.x - sp.nx * 1.5), camera.position.z - (sp.z - sp.nz * 1.5)) < 1.6);
      if (spot && (spot !== wt.lastSpot || t - wt.lastAt > 90)) {
        const deer = [...W.does, ...W.stags];
        const busy = deer.some((a) => a.state === 'curious' || a.state === 'gaze');
        const free = deer.filter((a) => ['graze', 'look', 'walk', 'away'].includes(a.state));
        if (!busy && free.length) {
          const [gx, gz] = fromSpot(spot, 2.4, rand(-0.4, 0.4));
          const pick = free.sort((p1, p2) => Math.hypot(p1.x - gx, p1.z - gz) - Math.hypot(p2.x - gx, p2.z - gz))[0];
          if (pick.state === 'away') { pick.spot = spot; arrive(pick, SPECIES.deer, false); }
          pick.spot = spot; pick.tx = gx; pick.tz = gz; pick.state = 'curious';
          wt.lastSpot = spot; wt.lastAt = t;
        }
      }
    }

    // ground animals
    const groups = [[W.does, W.meshes.does], [W.stags, W.meshes.stags], [W.rabbits, W.meshes.rabbits], [W.foxes, W.meshes.foxes]];
    for (const [list, mesh] of groups) {
      let any = false;
      for (let i = 0; i < list.length; i++) {
        const a = list[i], sp = SPECIES[a.kind];
        updateGround(a, sp, ok[a.group], dt, w.wind, w.puddles || 0);
        writeGround(mesh, i, a, sp, m, q, p, s, up);
        any = any || a.scale > 0.001;
      }
      mesh.visible = any; // nobody out: skip the draw call (shared program, so no recompile)
      mesh.instanceMatrix.needsUpdate = true;
      mesh.geometry.attributes.aAnim.needsUpdate = true;
    }

    // birds: flocks circling the hall; they fly in from (and off to) far away, and now and then
    // the whole flock lands — on the glass roof (seen from inside, right overhead) or on the lawn
    // in front of a window — hops and pecks about for a while, then takes off again.
    const bm = W.meshes.birds, banim = bm.geometry.attributes.aAnim.array;
    let anyBird = false;
    for (const f of W.flocks) {
      const want = ok[f.raven ? 'raven' : 'songbird'];
      f.out = clamp(f.out + (want ? -1 : 1) * dt / 14, 0, 1);
      f.theta += f.omega * f.dir * (1 + w.wind * 0.7) * dt;
      if (!want || f.out > 0) f.mode = 'fly';
      else if ((f.timer -= dt) <= 0) {
        if (f.mode === 'fly') { choosePerch(f, W.birds); f.mode = 'perch'; f.timer = rand(18, 40); }
        else { f.mode = 'fly'; f.timer = rand(15, 45); }
      }
      const R = f.R + f.out * f.out * 240, H = f.H + f.out * 50 + Math.sin(t * 0.23 + f.seed) * 3;
      const cx = Math.cos(f.theta) * R, cz = Math.sin(f.theta) * R;
      for (let k = 0; k < f.count; k++) {
        const i = f.start + k, b = W.birds[i];
        b.land = clamp(b.land + (f.mode === 'perch' ? b.landRate : -b.landRate * 2.5) * dt, 0, 1);
        if (f.out > 0.97 && b.land === 0) { bm.setMatrixAt(i, HIDDEN); continue; }
        anyBird = true;
        const gust = 1 + w.wind * 1.5;
        // place in the flying formation…
        let x = cx + b.ox + Math.sin(t * 0.7 + b.ph) * 1.6 * gust;
        let y = H + b.oy + Math.sin(t * 1.1 + b.ph * 2) * 0.6 * gust;
        let z = cz + b.oz + Math.cos(t * 0.6 + b.ph) * 1.6 * gust;
        // …blended towards its perch while landing or perched
        const k1 = THREE.MathUtils.smoothstep(b.land, 0, 1);
        let peck = 0, hopY = 0;
        if (b.land > 0) {
          if (b.land >= 1) {
            // on the perch: hop, peck or turn now and then
            b.act -= dt;
            if (b.act <= 0) {
              const r = Math.random();
              if (r < 0.35) { b.peckT = 0.5; }
              else if (r < 0.7) { // a little hop
                const a = Math.random() * TAU, d = rand(0.1, 0.35) * b.size;
                b.sx += Math.sin(a) * d; b.sz += Math.cos(a) * d;
                if (b.roof) { b.sx = clamp(b.sx, -7.6, 7.6); b.sz = clamp(b.sz, -7.6, 7.6); }
                else if (Math.abs(b.sx) < 13.5 && Math.abs(b.sz) < 13.5) { b.sx -= Math.sin(a) * d; b.sz -= Math.cos(a) * d; } // not into the wall
                b.hopT = 0.3; b.h = a;
              }
              else b.h += rand(-1.5, 1.5);
              b.act = rand(0.5, 2.2);
            }
            if (b.peckT > 0) { b.peckT -= dt; peck = Math.sin((1 - b.peckT / 0.5) * Math.PI) * 0.75; }
            if (b.hopT > 0) { b.hopT -= dt; hopY = Math.sin((1 - b.hopT / 0.3) * Math.PI) * 0.06 * b.size; }
          }
          const sy = b.roof ? roofHeightAt(b.sx, b.sz) + 0.005 : groundHeight(b.sx, b.sz) + 0.005;
          x += (b.sx - x) * k1; z += (b.sz - z) * k1;
          y += (sy - y) * Math.pow(k1, 0.8);
          y += hopY + 0.035 * b.size; // feet on the surface
        }
        const vx = x - b.px, vz = z - b.pz;
        if (b.land < 0.98 && vx * vx + vz * vz > 1e-6) b.h += wrapAngle(Math.atan2(vx, vz) - b.h) * Math.min(1, dt * 4);
        b.px = x; b.pz = z;
        // songbirds flap in bursts and glide between them; ravens flap slowly and soar; hard flapping to land
        const landing = b.land > 0.02 && b.land < 0.98;
        const flapping = landing || Math.sin(t * (f.raven ? 0.35 : 0.9) + b.ph) > (f.raven ? 0.1 : -0.3);
        const fold = THREE.MathUtils.smoothstep(b.land, 0.85, 1);
        b.phase += b.rate * dt * (flapping ? 1 : 0.15) * (1 - fold);
        e.set(0, b.h, Math.sin(t * 0.8 + b.ph) * 0.15 * gust * (1 - k1), 'YXZ');
        q.setFromEuler(e);
        p.set(x, y, z); s.setScalar(b.size);
        m.compose(p, q, s);
        bm.setMatrixAt(i, m);
        banim[i * 4] = b.phase;
        banim[i * 4 + 1] = flapping ? (f.raven ? 0.55 : 0.85) : 0.05;
        banim[i * 4 + 2] = fold > 0.5 ? peck : (flapping ? 0.1 : 0.12);
        banim[i * 4 + 3] = fold;
      }
    }
    bm.visible = anyBird;
    bm.instanceMatrix.needsUpdate = true;
    bm.geometry.attributes.aAnim.needsUpdate = true;

    // butterflies: erratic flight over the flowers outside each window
    const fm = W.meshes.flies, fanim = fm.geometry.attributes.aAnim.array;
    for (let i = 0; i < W.flies.length; i++) {
      const b = W.flies[i];
      updateButterfly(b, ok.butterfly, dt, w);
      if (b.scale <= 0.001) { fm.setMatrixAt(i, HIDDEN); continue; }
      e.set(0, b.h, 0, 'YXZ');
      q.setFromEuler(e);
      const resting = b.state === 'rest';
      p.set(b.x, b.y + (resting ? 0 : Math.sin(b.phase) * 0.025), b.z);
      s.setScalar(b.size * b.scale);
      m.compose(p, q, s);
      fm.setMatrixAt(i, m);
      fanim[i * 4] = b.phase;
      fanim[i * 4 + 1] = resting ? 0.3 : 1.05;
      fanim[i * 4 + 2] = resting ? 1.15 : 0.3;
      fanim[i * 4 + 3] = 0;
    }
    fm.visible = W.flies.some((b) => b.scale > 0.001);
    fm.instanceMatrix.needsUpdate = true;
    fm.geometry.attributes.aAnim.needsUpdate = true;

    // flowers fold away under snow (follows the slow snow cover of the landscape)
    W.snowCover += ((w.snow || 0) - W.snowCover) * dt * 0.12;
    const shown = Math.round(clamp(1 - (W.snowCover - 0.15) / 0.35, 0, 1) * 50) / 50;
    if (shown !== W.flowerShown) {
      W.flowerShown = shown;
      const fl = W.meshes.flowers;
      W.flowerSpots.forEach((f, i) => {
        if (shown <= 0) { fl.setMatrixAt(i, HIDDEN); return; }
        p.set(f.x, groundHeight(f.x, f.z) - 0.02, f.z);
        q.setFromAxisAngle(up, f.rot);
        s.set(f.s, f.s * shown, f.s);
        m.compose(p, q, s);
        fl.setMatrixAt(i, m);
      });
      fl.instanceMatrix.needsUpdate = true;
    }

    // the secret night owl on the glass roof, turning its head now and then
    const owl = W.meshes.owl;
    if (eggState.owl) {
      p.set(1.2, roofHeightAt(1.2, 1.5) + 0.01, 1.5);
      q.setFromAxisAngle(up, 0.25); // near the middle of the roof, looking towards the entrance
      s.setScalar(2.2);
      m.compose(p, q, s);
      owl.setMatrixAt(0, m);
      const oa = owl.geometry.attributes.aAnim.array;
      oa[3] = Math.sin(t * 0.45) * 2.2 + Math.sin(t * 1.7) * 0.3; // head swivel
      owl.geometry.attributes.aAnim.needsUpdate = true;
      // glowing eyes at night, following the head as it turns (same swivel as the shader's)
      const yaw = 0.35 * Math.sin(oa[3]);
      const ep = W.eyeGeo.attributes.position.array, ec = W.eyeGeo.attributes.color.array;
      const glowAmt = THREE.MathUtils.smoothstep(n, 0.5, 0.9) * 1.6;
      [-1, 1].forEach((sd, k) => {
        const lx = sd * 0.045, lz = 0.1; // eye, relative to the neck (0, 0.34, 0)
        const ex = lx * Math.cos(yaw) + lz * Math.sin(yaw), ez = -lx * Math.sin(yaw) + lz * Math.cos(yaw);
        tmp.current.p.set(ex, 0.425, ez).applyMatrix4(m);
        ep[k * 3] = tmp.current.p.x; ep[k * 3 + 1] = tmp.current.p.y; ep[k * 3 + 2] = tmp.current.p.z;
        ec[k * 3] = glowAmt; ec[k * 3 + 1] = glowAmt * 0.75; ec[k * 3 + 2] = glowAmt * 0.1;
      });
      W.eyeGeo.setDrawRange(0, glowAmt > 0.01 ? 2 : 0);
      W.eyeGeo.attributes.position.needsUpdate = true;
      W.eyeGeo.attributes.color.needsUpdate = true;
    } else { owl.setMatrixAt(0, HIDDEN); W.eyeGeo.setDrawRange(0, 0); }
    owl.instanceMatrix.needsUpdate = true;

    // fireflies: short yellow-green flashes on calm, dry nights
    W.ffPresence = ease(W.ffPresence, ok.firefly ? 1 : 0, 0.5, dt);
    const ffOn = W.ffPresence > 0.01;
    W.ffGeo.setDrawRange(0, ffOn ? W.fireflies.length : 0);
    if (ffOn) {
      const pos = W.ffGeo.attributes.position.array, col = W.ffGeo.attributes.color.array;
      W.fireflies.forEach((f, i) => {
        pos[i * 3] = f.bx + Math.sin(t * 0.3 + f.p1) * 1.3;
        pos[i * 3 + 1] = f.by + Math.sin(t * 0.5 + f.p2) * 0.4;
        pos[i * 3 + 2] = f.bz + Math.cos(t * 0.27 + f.p3) * 1.3;
        const b = W.ffPresence * Math.pow(Math.max(0, Math.sin(t * f.rate + f.ph)), 6) * 2;
        col[i * 3] = 0.8 * b; col[i * 3 + 1] = 1.0 * b; col[i * 3 + 2] = 0.25 * b;
      });
      W.ffGeo.attributes.position.needsUpdate = true;
      W.ffGeo.attributes.color.needsUpdate = true;
    }
  });

  return (
    <group>
      {Object.entries(world.meshes).map(([k, mesh]) => <primitive key={k} object={mesh} />)}
      <primitive object={world.ffPoints} />
      <primitive object={world.eyePoints} />
    </group>
  );
};

// ------------------------------------------------------------------ bird perches
// Half the time the glass roof (its flat top, right above the visitors), otherwise the lawn in
// front of one of the windows. Each bird gets its own spot near the flock's landing site.
function choosePerch(f, birds) {
  const roof = Math.random() < 0.5;
  const sp = WINDOW_SPOTS[Math.floor(Math.random() * WINDOW_SPOTS.length)];
  const cx = rand(-5, 5), cz = rand(-5, 5), out = rand(4, 8), lat = rand(-2, 2);
  for (let k = 0; k < f.count; k++) {
    const b = birds[f.start + k];
    b.roof = roof;
    if (roof) { b.sx = clamp(cx + rand(-1.6, 1.6), -7.6, 7.6); b.sz = clamp(cz + rand(-1.6, 1.6), -7.6, 7.6); }
    else [b.sx, b.sz] = fromSpot(sp, Math.max(2.2, out + rand(-1.3, 1.3)), lat + rand(-1.6, 1.6));
    b.act = rand(0.5, 2);
  }
}

// ------------------------------------------------------------------ butterfly behaviour
function pickFlyTarget(b, toGround) {
  const sp = b.spot;
  const out = rand(2.2, 8), along = rand(-3.5, 3.5);
  b.tx = sp.x + sp.nx * out + sp.tx * along;
  b.tz = sp.z + sp.nz * out + sp.tz * along;
  b.ty = groundHeight(b.tx, b.tz) + (toGround ? 0.32 : rand(0.6, 3.2));
  b.timer = rand(0.8, 2.6);
}

function updateButterfly(b, want, dt, w) {
  const wind = w.wind || 0, wx = w.windX ?? 1, wz = w.windZ ?? 0; // pushed along the wind
  if (b.state === 'away') {
    if (want) {
      b.timer -= dt;
      if (b.timer <= 0) { pickFlyTarget(b, true); b.x = b.tx; b.y = b.ty - 0.25; b.z = b.tz; b.state = 'fly'; b.scale = 0; pickFlyTarget(b, false); }
    }
    return;
  }
  if (!want && b.state !== 'hide') { b.state = 'hide'; pickFlyTarget(b, true); b.ty -= 0.25; }
  if (want && b.state === 'hide') { b.state = 'fly'; pickFlyTarget(b, false); }

  b.timer -= dt;
  if (b.state === 'fly') {
    b.scale = Math.min(1, b.scale + dt * 1.2);
    if (b.timer <= 0) {
      const land = Math.random() < 0.15;
      pickFlyTarget(b, land);
      b.landing = land;
    }
    if (b.landing && Math.hypot(b.tx - b.x, b.ty - b.y, b.tz - b.z) < 0.15) { b.state = 'rest'; b.timer = rand(2, 6); b.vx = b.vy = b.vz = 0; }
  } else if (b.state === 'rest') {
    if (b.timer <= 0) { b.state = 'fly'; b.landing = false; pickFlyTarget(b, false); }
  } else if (b.state === 'hide') {
    if (Math.hypot(b.tx - b.x, b.ty - b.y, b.tz - b.z) < 0.3) b.scale = Math.max(0, b.scale - dt * 0.9);
    if (b.scale <= 0) { b.state = 'away'; b.timer = rand(1, 10); }
  }

  if (b.state !== 'rest') {
    // steer towards the target with a jittery, fluttering path
    const k = b.landing || b.state === 'hide' ? 2.2 : 1.4;
    b.vx += ((b.tx - b.x) * k - b.vx * 1.3 + (Math.random() - 0.5) * 7 + wind * 1.5 * wx) * dt;
    b.vy += ((b.ty - b.y) * k - b.vy * 1.3 + (Math.random() - 0.5) * 5) * dt;
    b.vz += ((b.tz - b.z) * k - b.vz * 1.3 + (Math.random() - 0.5) * 7 + wind * 1.5 * wz) * dt;
    const sp = Math.hypot(b.vx, b.vy, b.vz), max = 1.7;
    if (sp > max) { b.vx *= max / sp; b.vy *= max / sp; b.vz *= max / sp; }
    b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
    // never through the wall: keep at least 1.8 m outside it
    const o = (b.x - b.spot.x) * b.spot.nx + (b.z - b.spot.z) * b.spot.nz;
    if (o < 1.8) { b.x += b.spot.nx * (1.8 - o); b.z += b.spot.nz * (1.8 - o); }
    b.y = Math.max(b.y, groundHeight(b.x, b.z) + 0.1);
    if (Math.hypot(b.vx, b.vz) > 0.05) b.h += wrapAngle(Math.atan2(b.vx, b.vz) - b.h) * Math.min(1, dt * 6);
    b.phase += dt * 24;
  } else {
    b.phase += dt * 2.5;
  }
}

export default Wildlife;
